// @ts-nocheck
import { useEffect, useState } from 'react';

import { Check, CaretDown } from '@phosphor-icons/react';
import { Text, XStack, YStack } from 'tamagui';

import { Header, Footer, SubscribeForm } from '../components/landing';
import { PRICING_TIERS } from '../components/landing/constants';

const FEATURE_COMPARISON = [
  { feature: 'Connected apps', free: '3', pro: 'Unlimited', team: 'Unlimited' },
  { feature: 'Users', free: '1', pro: '1', team: 'Unlimited' },
  { feature: 'Search', free: true, pro: true, team: true },
  { feature: 'Kanban', free: true, pro: true, team: true },
  { feature: 'Calendar', free: true, pro: true, team: true },
  { feature: 'Documents', free: true, pro: true, team: true },
  { feature: 'Wiki', free: true, pro: true, team: true },
  { feature: 'OKRs', free: false, pro: true, team: true },
  { feature: 'Sprints', free: false, pro: true, team: true },
  { feature: 'API access', free: false, pro: true, team: true },
  { feature: 'SSO / SAML', free: false, pro: false, team: true },
  { feature: 'Priority support', free: false, pro: false, team: true },
];

const FAQS = [
  {
    q: 'Is there really a free tier?',
    a: 'Yes. 3 connected apps, 1 user, forever. No credit card required, no time limit.',
  },
  {
    q: 'Can I change plans later?',
    a: 'Yes, upgrade or downgrade anytime. Changes take effect immediately and billing is prorated.',
  },
  {
    q: 'What counts as a connected app?',
    a: 'Each OAuth integration (Gmail, Slack, Notion, GitHub, etc.) counts as one connected app.',
  },
  {
    q: 'Do you offer annual billing?',
    a: 'Yes. Annual plans include 2 months free, saving you 17% compared to monthly billing.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'All major credit cards via Stripe. We also support invoicing for Team plans.',
  },
  {
    q: 'Is there an enterprise plan?',
    a: 'Yes. For custom pricing, volume discounts, and dedicated support, contact us at dans@romans.dev.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. No contracts, no penalties. Cancel anytime and you keep access through the end of your billing period.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. Permissions-aware search means users only see what they can access. SSO support on Team plans. All data encrypted in transit and at rest.',
  },
];

export default function PricingPage() {
  const [isMobile, setIsMobile] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderCell = (value: boolean | string) => {
    if (typeof value === 'string') {
      return (
        <Text fontSize={14} color="$color11" fontWeight="500">
          {value}
        </Text>
      );
    }
    return (
      <Text fontSize={16} color={value ? '#ededed' : '$color6'}>
        {value ? '\u2713' : '\u2014'}
      </Text>
    );
  };

  return (
    <YStack backgroundColor="$background" minHeight="100vh">
      <Header />

      <YStack paddingTop={140} paddingHorizontal="$5" gap="$8" paddingBottom={80}>
        <YStack maxWidth={1100} alignSelf="center" width="100%">
          {/* Page header */}
          <YStack alignItems="center" marginBottom={60}>
            <Text
              fontSize={13}
              fontWeight="600"
              color="#ededed"
              letterSpacing={2}
              textTransform="uppercase"
              marginBottom="$3"
            >
              Pricing
            </Text>
            <Text
              fontSize={isMobile ? 30 : 44}
              fontWeight="700"
              textAlign="center"
              letterSpacing={-1.5}
              lineHeight={isMobile ? 36 : 52}
              color="$color12"
            >
              Simple, transparent pricing
            </Text>
            <Text
              fontSize={isMobile ? 16 : 18}
              color="$color9"
              textAlign="center"
              marginTop="$3"
              lineHeight={28}
            >
              Start free. Upgrade when you need more.
            </Text>
          </YStack>

          {/* Tier cards */}
          <XStack
            gap="$4"
            justifyContent="center"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'center' : 'stretch'}
            marginBottom={80}
          >
            {PRICING_TIERS.map((tier) => (
              <YStack
                key={tier.name}
                width={isMobile ? '100%' : 320}
                maxWidth={isMobile ? 400 : undefined}
                padding="$6"
                borderRadius="$3"
                borderWidth={tier.highlighted ? 2 : 1}
                borderColor={tier.highlighted ? '$color6' : '$color4'}
                backgroundColor="$color2"
                gap="$4"
                position="relative"
                style={
                  tier.highlighted
                    ? { boxShadow: '0 0 40px rgba(255, 255, 255, 0.06)' }
                    : undefined
                }
              >
                {tier.highlighted && (
                  <XStack
                    position="absolute"
                    top={-13}
                    alignSelf="center"
                    paddingHorizontal="$4"
                    paddingVertical="$1"
                    borderRadius="$3"
                    backgroundColor="#ededed"
                  >
                    <Text fontSize={12} color="#000" fontWeight="600">
                      Most Popular
                    </Text>
                  </XStack>
                )}

                <Text fontSize={18} fontWeight="600" color="$color12">
                  {tier.name}
                </Text>

                <XStack alignItems="baseline" gap="$1">
                  <Text
                    fontSize={48}
                    fontWeight="700"
                    color="$color12"
                    letterSpacing={-2}
                    lineHeight={52}
                  >
                    {tier.price}
                  </Text>
                  <Text fontSize={16} color="$color8" fontWeight="400">
                    {tier.period}
                  </Text>
                </XStack>

                <Text fontSize={14} color="$color9" lineHeight={22}>
                  {tier.description}
                </Text>

                <YStack height={1} backgroundColor="$color4" marginVertical="$2" />

                <YStack gap="$3">
                  {tier.features.map((feature) => (
                    <XStack key={feature} gap="$3" alignItems="center">
                      <Check size={16} color={tier.highlighted ? '#ededed' : 'var(--color8)'} />
                      <Text fontSize={14} color="$color10" lineHeight={20}>
                        {feature}
                      </Text>
                    </XStack>
                  ))}
                </YStack>
              </YStack>
            ))}
          </XStack>

          <Text fontSize={14} color="$color7" textAlign="center" marginBottom={80}>
            All paid plans include a 14-day free trial. Annual plans save 17%.
          </Text>

          {/* Feature comparison table */}
          <YStack marginBottom={80}>
            <Text
              fontSize={isMobile ? 24 : 32}
              fontWeight="700"
              textAlign="center"
              letterSpacing={-1}
              color="$color12"
              marginBottom={40}
            >
              Feature comparison
            </Text>

            <YStack
              borderRadius="$3"
              borderWidth={1}
              borderColor="$color4"
              overflow="hidden"
            >
              {/* Table header */}
              <XStack
                backgroundColor="$color2"
                paddingVertical="$3"
                paddingHorizontal="$4"
                borderBottomWidth={1}
                borderColor="$color4"
              >
                <div style={{ flex: 2 }}>
                  <Text fontSize={13} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={1}>
                    Feature
                  </Text>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <Text fontSize={13} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={1}>
                    Free
                  </Text>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <Text fontSize={13} fontWeight="600" color="#ededed" textTransform="uppercase" letterSpacing={1}>
                    Pro
                  </Text>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <Text fontSize={13} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={1}>
                    Team
                  </Text>
                </div>
              </XStack>

              {/* Table rows */}
              {FEATURE_COMPARISON.map((row, i) => (
                <XStack
                  key={row.feature}
                  paddingVertical="$3"
                  paddingHorizontal="$4"
                  borderBottomWidth={i < FEATURE_COMPARISON.length - 1 ? 1 : 0}
                  borderColor="$color3"
                  hoverStyle={{ backgroundColor: '$color2' }}
                >
                  <div style={{ flex: 2 }}>
                    <Text fontSize={14} color="$color11">
                      {row.feature}
                    </Text>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center' }}>{renderCell(row.free)}</div>
                  <div style={{ flex: 1, textAlign: 'center' }}>{renderCell(row.pro)}</div>
                  <div style={{ flex: 1, textAlign: 'center' }}>{renderCell(row.team)}</div>
                </XStack>
              ))}
            </YStack>
          </YStack>

          {/* FAQ */}
          <YStack marginBottom={80}>
            <Text
              fontSize={isMobile ? 24 : 32}
              fontWeight="700"
              textAlign="center"
              letterSpacing={-1}
              color="$color12"
              marginBottom={40}
            >
              Frequently asked questions
            </Text>

            <YStack maxWidth={700} alignSelf="center" width="100%" gap="$2">
              {FAQS.map((faq, i) => (
                <YStack
                  key={i}
                  borderWidth={1}
                  borderColor="$color4"
                  borderRadius="$3"
                  overflow="hidden"
                >
                  <XStack
                    paddingVertical="$4"
                    paddingHorizontal="$4"
                    alignItems="center"
                    justifyContent="space-between"
                    cursor="pointer"
                    hoverStyle={{ backgroundColor: '$color2' }}
                    onPress={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <Text fontSize={15} fontWeight="500" color="$color12" flex={1}>
                      {faq.q}
                    </Text>
                    <CaretDown
                      size={18}
                      color="var(--color8)"
                      style={{
                        transition: 'transform 0.2s ease',
                        transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0deg)',
                        flexShrink: 0,
                        marginLeft: 12,
                      } as any}
                    />
                  </XStack>
                  {openFaq === i && (
                    <YStack paddingHorizontal="$4" paddingBottom="$4">
                      <Text fontSize={14} color="$color9" lineHeight={22}>
                        {faq.a}
                      </Text>
                    </YStack>
                  )}
                </YStack>
              ))}
            </YStack>
          </YStack>

          {/* CTA */}
          <YStack alignItems="center" gap="$4">
            <Text
              fontSize={isMobile ? 22 : 28}
              fontWeight="700"
              textAlign="center"
              letterSpacing={-0.5}
              color="$color12"
            >
              Ready to get started?
            </Text>
            <Text fontSize={16} color="$color9" textAlign="center" marginBottom="$2">
              Join the waitlist and be the first to know when Torch launches.
            </Text>
            <SubscribeForm />
          </YStack>
        </YStack>
      </YStack>

      <Footer />
    </YStack>
  );
}
